import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Milestone, Building2, Trees, DoorOpen } from "lucide-react";
import { Reveal } from "./Reveal";

const FEATURES = [
  { icon: Building2, label: "36 Planned Plots" },
  { icon: Milestone, label: "30 ft Wide Internal Roads" },
  { icon: DoorOpen, label: "Grand Gated Entry" },
  { icon: Trees, label: "Water Nahar & Green Belt" },
];

export const ProjectPlan = () => {
  const [zoom, setZoom] = useState(false);

  return (
    <section
      id="project-plan"
      data-testid="project-plan-section-container"
      className="py-20 md:py-28 px-4 sm:px-8 lg:px-16 bg-[#EAE0D5]"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#C15B3D] mb-4">
              Master Layout
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1F291E] leading-tight">
              Project Plan
            </h2>
            <p className="mt-5 text-lg text-[#5C6B5A]">
              A thoughtfully planned layout of 36 farmhouse plots with wide
              roads, a grand entry, and a green belt along the water nahar.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="grid lg:grid-cols-4 gap-6 md:gap-8 mb-10">
            {FEATURES.map((f, i) => (
              <div
                key={f.label}
                data-testid={`project-plan-feature-${i}`}
                className="flex items-center gap-4 rounded-2xl bg-[#F7F3EE] border border-[#D5C8B8] p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                <div className="h-11 w-11 shrink-0 rounded-xl bg-[#2C5F2D] grid place-items-center">
                  <f.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                </div>
                <span className="font-medium text-[#1F291E] text-[15px]">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <button
            data-testid="project-plan-image-button"
            onClick={() => setZoom(true)}
            className="group relative block w-full rounded-3xl overflow-hidden shadow-2xl border border-[#D5C8B8] bg-white"
          >
            <img
              src="/project-plan.jpg"
              alt="Aaroh Farms master layout plan"
              className="w-full object-contain"
            />
            <span className="absolute top-4 right-4 inline-flex items-center gap-2 rounded-full bg-[#1F291E]/70 backdrop-blur-sm px-4 py-2 text-xs font-semibold text-white opacity-80 transition-opacity group-hover:opacity-100">
              <Maximize2 className="h-4 w-4" />
              Click to zoom
            </span>
          </button>
        </Reveal>
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="project-plan-zoom-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(false)}
            className="fixed inset-0 z-[70] grid place-items-center bg-[#142415]/90 backdrop-blur-sm p-4 sm:p-8"
          >
            <button
              data-testid="project-plan-zoom-close"
              onClick={() => setZoom(false)}
              aria-label="Close"
              className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 grid place-items-center text-white transition-colors hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              onClick={(e) => e.stopPropagation()}
              src="/project-plan.jpg"
              alt="Aaroh Farms master layout plan — enlarged"
              className="max-h-[90vh] max-w-full rounded-xl object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
